import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, Globe, Menu, Phone, X } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/languageStore';
import { useWebsite } from '../contexts/websiteStore';
import { Link } from '../router/Link';
import { useRouter } from '../router/Router';

export function Header() {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [langMenuOpen, setLangMenuOpen] = useState(false);
    const langMenuRef = useRef<HTMLDivElement>(null);

    const { t, language, setLanguage, isRTL } = useLanguage();
    const { config, getAsset } = useWebsite();
    const { pathname, navigate } = useRouter();

    // Get website-specific header styles
    const getHeaderStyles = () => {
        const websiteName = config.name.toLowerCase();

        switch (websiteName) {
            case 'bluesky':
                return {
                    // Modern, clean design with pill-shaped elements
                    navButtonClass: 'rounded-full px-6 py-2',
                    ctaButtonClass: 'rounded-full shadow-lg',
                    languageButtonClass: 'rounded-full',
                    mobileButtonClass: 'rounded-full',
                    linkHoverEffect: 'hover:bg-white/20 hover:backdrop-blur-sm hover:scale-105',
                    activeLinkClass: 'bg-white/30 backdrop-blur-sm border border-white/40 shadow-md',
                    buttonTransition: 'transition-all duration-300 ease-out'
                };
            case 'emcor':
                return {
                    // Industrial, bold design with moderate rounded elements
                    navButtonClass: 'rounded-lg px-5 py-2 border border-red-300/40',
                    ctaButtonClass: 'rounded-lg shadow-xl border border-transparent',
                    languageButtonClass: 'rounded-lg border border-red-300/40',
                    mobileButtonClass: 'rounded-lg border border-red-300/40',
                    linkHoverEffect: 'hover:bg-red-500/20 hover:border-red-500/60 hover:shadow-md',
                    activeLinkClass: 'bg-red-500/30 border-red-500/70 shadow-lg',
                    buttonTransition: 'transition-all duration-250 ease-in-out'
                };
            case 'basrasky':
                return {
                    // Eco-friendly, organic design with leaf-like shapes
                    navButtonClass: 'rounded-2xl px-5 py-2 border border-green-200/30',
                    ctaButtonClass: 'rounded-2xl shadow-lg',
                    languageButtonClass: 'rounded-xl',
                    mobileButtonClass: 'rounded-xl',
                    linkHoverEffect: 'hover:bg-green-500/20 hover:scale-110 hover:border-green-400/60',
                    activeLinkClass: 'bg-green-500/30 border border-green-500/70 shadow-lg',
                    buttonTransition: 'transition-all duration-400 ease-in-out'
                };
            default:
                return {
                    navButtonClass: 'rounded-lg',
                    ctaButtonClass: 'rounded-lg',
                    languageButtonClass: 'rounded-lg',
                    mobileButtonClass: 'rounded-lg',
                    linkHoverEffect: 'hover:bg-primary/20',
                    activeLinkClass: 'bg-primary/30',
                    buttonTransition: 'transition-all duration-300'
                };
        }
    };

    const headerStyles = getHeaderStyles();

    // Check if we're on homepage or careers page
    const isHomePage = pathname === '/' || pathname === '/en' || pathname === '/ar';
    const isCareersPage = pathname === '/careers' || pathname === '/en/careers' || pathname === '/ar/careers';
    const shouldUseHomepageStyle = isHomePage || isCareersPage;

    // Navigation items
    const navItems = [
        { name: t('navigation.home'), href: '/' },
        { name: t('navigation.about_us'), href: '/about-us' },
        { name: t('navigation.services'), href: '/services' },
        { name: t('navigation.news'), href: '/news' },
        { name: t('navigation.careers'), href: '/careers' },
        { name: t('navigation.contact'), href: '/contact' },
    ];

    // Handle scroll effect
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close language menu on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (langMenuRef.current && !langMenuRef.current.contains(event.target as Node)) {
                setLangMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Handle home click
    const handleHomeClick = () => {
        const homePath = language === 'en' ? '/en' : '/ar';
        if (pathname !== homePath && pathname !== '/') {
            navigate(homePath);
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        setIsOpen(false);
    };

    // Handle language change
    const changeLanguage = (newLang: 'en' | 'ar') => {
        setLanguage(newLang);
        setLangMenuOpen(false);
        setIsOpen(false);

        // Get current path without language prefix
        let currentPath = pathname;
        if (currentPath.startsWith('/ar/') || currentPath.startsWith('/en/')) {
            currentPath = currentPath.substring(3);
        } else if (currentPath === '/ar' || currentPath === '/en') {
            currentPath = '/';
        }

        // Construct new path with language prefix
        const newPath = newLang === 'en'
            ? (currentPath === '/' ? '/en' : `/en${currentPath}`)
            : (currentPath === '/' ? '/ar' : `/ar${currentPath}`);

        // Navigate to new URL
        window.location.href = newPath;
    };

    return (
        <motion.header
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 py-4 ${scrolled
                ? 'bg-white/80 backdrop-blur-md shadow-lg border-b border-gray-200/20'
                : 'bg-primary/50 backdrop-blur-lg'
                }`}
            style={{ boxShadow: scrolled ? '0 2px 16px rgba(0,0,0,0.06)' : 'none' }}
        >
            <nav className="container mx-auto px-4 sm:px-8 py-1.5">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <motion.button
                        onClick={handleHomeClick}
                        whileHover={{ scale: 1.08 }}
                        transition={{ duration: 0.2 }}
                        className="flex flex-col items-center group"
                        style={{ zIndex: 2 }}
                        aria-label="Home"
                    >
                        <img
                            src={getAsset(config.assets.logo)}
                            alt={`${config.name} Logo`}
                            style={{
                                width: config.name === 'Bluesky' ? '50px' : config.name === 'Basrasky' ? '50px' : '100px',
                                height: config.name === 'Bluesky' ? '50px' : config.name === 'Basrasky' ? '50px' : '100px',
                                objectFit: 'contain',
                                transition: 'all 300ms',
                                marginTop: '8px',
                                marginBottom: '8px',
                                filter: config.name === 'Basrasky'
                                    ? 'brightness(0) saturate(100%) invert(20%) sepia(100%) saturate(2000%) hue-rotate(120deg) brightness(0.6) contrast(1.2)'
                                    : 'none'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.1)';
                                e.currentTarget.style.filter = config.name === 'Basrasky'
                                    ? 'brightness(0) saturate(100%) invert(20%) sepia(100%) saturate(2000%) hue-rotate(120deg) brightness(0.6) contrast(1.2)'
                                    : 'none';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.filter = config.name === 'Basrasky'
                                    ? 'brightness(0) saturate(100%) invert(20%) sepia(100%) saturate(2000%) hue-rotate(120deg) brightness(0.6) contrast(1.2)'
                                    : 'none';
                            }}
                        />
                        {config.name === 'Bluesky' && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.3 }}
                                className={`text-xs font-bold tracking-wider uppercase transition-colors duration-300 
                                    text-blue-800 mt-[-10px]`}
                                style={{
                                    textShadow: scrolled || !shouldUseHomepageStyle
                                        ? 'none'
                                        : '0 1px 3px rgba(0,0,0,0.3)'
                                }}
                            >
                                BLUE SKY ENERGY
                            </motion.div>
                        )}
                        {config.name === 'Basrasky' && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.3 }}
                                className={`text-xs font-bold tracking-wider uppercase transition-colors duration-300 
                                    mt-[-10px]`}
                                style={{
                                    color: '#004600',
                                    textShadow: scrolled || !shouldUseHomepageStyle
                                        ? 'none'
                                        : '0 1px 3px rgba(0,0,0,0.3)'
                                }}
                            >
                                BASRA SKY ENERGY
                            </motion.div>
                        )}
                    </motion.button>

                    {/* Desktop Navigation */}
                    <div className={`hidden lg:flex items-center gap-10 ${isRTL ? 'mr-10' : 'ml-10'}`}>
                        {navItems.map((item, index) => {
                            // Check if current path matches the item href, ignoring language prefix
                            const isActive = pathname === item.href ||
                                pathname === `/en${item.href}` ||
                                pathname === `/ar${item.href}` ||
                                (item.href === '/' && (pathname === '/en' || pathname === '/ar'));
                            const baseClass = `relative font-light text-xs tracking-wide ${headerStyles.navButtonClass} ${headerStyles.buttonTransition} ${isActive
                                ? scrolled || !shouldUseHomepageStyle
                                    ? `font-semibold text-primary ${headerStyles.activeLinkClass}`
                                    : `font-semibold text-white ${headerStyles.activeLinkClass}`
                                : scrolled || !shouldUseHomepageStyle
                                    ? `text-gray-800 ${headerStyles.linkHoverEffect}`
                                    : `text-white ${headerStyles.linkHoverEffect}`
                                }`;

                            return (
                                <motion.button
                                    key={item.name}
                                    onClick={() => {
                                        const targetPath = language === 'en'
                                            ? (item.href === '/' ? '/en' : `/en${item.href}`)
                                            : (item.href === '/' ? '/ar' : `/ar${item.href}`);

                                        if (pathname !== targetPath) {
                                            window.location.href = targetPath;
                                        } else {
                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                        }
                                    }}
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.8, delay: index * 0.1, ease: 'easeOut' }}
                                    className={baseClass}
                                >
                                    {item.name}
                                </motion.button>
                            );
                        })}
                    </div>

                    {/* Right: Phone & Language Dropdown */}
                    <div className="hidden lg:flex items-center gap-6 relative">
                        {/* Phone Number */}
                        <div
                            className={`flex items-center font-light text-xs tracking-wide transition-colors duration-300 ${isRTL ? 'ml-2' : 'mr-2'} ${scrolled || !shouldUseHomepageStyle ? 'text-gray-800' : 'text-white'}`}
                        >
                            <Phone className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <span style={{ direction: 'ltr', unicodeBidi: 'embed' }}>
                                {t('navigation.phone')}
                            </span>
                        </div>

                        {/* CTA Button */}
                        <Link
                            to={language === 'en' ? '/en/contact' : '/ar/contact'}
                            className={`px-6 py-2 ${headerStyles.ctaButtonClass} text-sm font-medium ${headerStyles.buttonTransition} ${scrolled
                                ? 'bg-primary hover:bg-primary/90 text-white'
                                : 'bg-white hover:bg-primary/10 text-primary'
                                }`}
                        >
                            {t('navigation.get_quote')}
                        </Link>

                        {/* Language Dropdown */}
                        <div ref={langMenuRef} className="relative">
                            <button
                                onClick={() => setLangMenuOpen(!langMenuOpen)}
                                className={`flex items-center gap-1 text-xs font-light px-3 py-1 ${headerStyles.languageButtonClass} border ${headerStyles.buttonTransition} focus:outline-none focus:ring-2 focus:ring-primary ${scrolled || !shouldUseHomepageStyle
                                    ? 'text-gray-800 border-gray-300 bg-white hover:bg-gray-50'
                                    : 'text-white border-white/30 bg-white/20 hover:bg-white/30'
                                    }`}
                                aria-haspopup="true"
                                aria-expanded={langMenuOpen}
                                aria-label="Select Language"
                            >
                                <Globe className={`w-5 h-5 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                                <span>{language === 'ar' ? 'عربي' : 'EN'}</span>
                                <ChevronDown
                                    className={`w-4 h-4 transition-transform ${langMenuOpen ? 'rotate-180' : 'rotate-0'
                                        }`}
                                />
                            </button>

                            <AnimatePresence>
                                {langMenuOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.95 }}
                                        transition={{ duration: 0.2 }}
                                        className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-32 bg-white rounded-md shadow-lg border border-gray-200 z-50`}
                                    >
                                        <button
                                            onClick={() => changeLanguage('en')}
                                            className={`w-full text-left px-4 py-2 text-sm hover:bg-primary hover:text-white rounded-t-md ${language === 'en' ? 'bg-primary text-white' : 'text-gray-800'
                                                }`}
                                        >
                                            🇬🇧 English
                                        </button>
                                        <button
                                            onClick={() => changeLanguage('ar')}
                                            className={`w-full text-left px-4 py-2 text-sm hover:bg-primary hover:text-white rounded-b-md ${language === 'ar' ? 'bg-primary text-white' : 'text-gray-800'
                                                }`}
                                        >
                                            🇦🇪 عربي
                                        </button>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>

                    {/* Mobile Menu Button */}
                    <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => setIsOpen(!isOpen)}
                        className={`lg:hidden transition-colors duration-300 ${scrolled ? 'text-gray-800 hover:text-gray-600' : 'text-white hover:text-white/80'}`}
                        aria-label={isOpen ? 'Close menu' : 'Open menu'}
                    >
                        {isOpen ? <X size={24} /> : <Menu size={24} />}
                    </motion.button>
                </div>

                {/* Mobile Menu */}
                <AnimatePresence>
                    {isOpen && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: '100vh' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="lg:hidden fixed inset-0 z-40 bg-white/95 backdrop-blur-md border-t border-gray-200/50 shadow-xl overflow-y-auto"
                        >
                            <div className="flex justify-end p-4">
                                <button
                                    onClick={() => setIsOpen(false)}
                                    className="text-gray-500 hover:text-primary transition duration-200"
                                    aria-label="Close mobile menu"
                                >
                                    <X size={28} />
                                </button>
                            </div>

                            <div className="px-4 pb-6 space-y-6 flex flex-col items-center">
                                {/* Phone number for mobile */}
                                <div className="flex items-center font-light text-base tracking-wide transition-colors duration-300 text-gray-800 mb-2">
                                    <Phone className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                                    <bdi className="text-primary">
                                        {t('navigation.phone')}
                                    </bdi>
                                </div>

                                {/* Mobile Navigation Items */}
                                {navItems.map((item) => {
                                    // Check if current path matches the item href, ignoring language prefix
                                    const isActive = pathname === item.href ||
                                        pathname === `/en${item.href}` ||
                                        pathname === `/ar${item.href}` ||
                                        (item.href === '/' && (pathname === '/en' || pathname === '/ar'));
                                    const baseClass = `w-full text-center font-light text-lg tracking-wide py-3 px-4 ${headerStyles.mobileButtonClass} ${headerStyles.buttonTransition} relative ${isActive
                                        ? `after:absolute after:bottom-0 after:left-1/2 after:transform after:-translate-x-1/2 after:w-16 after:h-0.5 after:bg-gradient-to-r after:from-primary after:to-primary after:scale-x-100 after:origin-left after:transition-transform after:duration-300 ${headerStyles.activeLinkClass}`
                                        : `text-gray-800 hover:text-primary after:absolute after:bottom-0 after:left-1/2 after:transform after:-translate-x-1/2 after:w-16 after:h-0.5 after:bg-gradient-to-r after:from-primary after:to-primary after:scale-x-0 after:origin-left hover:after:scale-x-100 after:transition-transform after:duration-300 ${headerStyles.linkHoverEffect}`
                                        }`;

                                    return (
                                        <button
                                            key={item.name}
                                            onClick={() => {
                                                const targetPath = language === 'en'
                                                    ? (item.href === '/' ? '/en' : `/en${item.href}`)
                                                    : (item.href === '/' ? '/ar' : `/ar${item.href}`);

                                                if (pathname !== targetPath) {
                                                    window.location.href = targetPath;
                                                } else {
                                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                                }
                                                setIsOpen(false);
                                            }}
                                            className={baseClass}
                                        >
                                            {item.name}
                                        </button>
                                    );
                                })}

                                {/* Mobile CTA Button */}
                                <div onClick={() => setIsOpen(false)}>
                                    <Link
                                        to={language === 'en' ? '/en/contact' : '/ar/contact'}
                                        className={`w-full bg-primary hover:bg-primary/90 text-white px-6 py-3 ${headerStyles.ctaButtonClass} text-lg font-medium tracking-wide shadow-lg text-center ${headerStyles.buttonTransition}`}
                                    >
                                        {t('navigation.get_quote')}
                                    </Link>
                                </div>

                                {/* Mobile Language Switcher */}
                                <div className="flex gap-4 mt-4">
                                    <button
                                        onClick={() => changeLanguage('en')}
                                        className={`px-6 py-2 ${headerStyles.languageButtonClass} text-sm font-light tracking-wide border ${headerStyles.buttonTransition} ${language === 'en'
                                            ? 'bg-primary text-white border-primary'
                                            : 'border-gray-300 text-gray-800 hover:bg-primary hover:text-white'
                                            }`}
                                    >
                                        🇬🇧 English
                                    </button>
                                    <button
                                        onClick={() => changeLanguage('ar')}
                                        className={`px-6 py-2 ${headerStyles.languageButtonClass} text-sm font-light tracking-wide border ${headerStyles.buttonTransition} ${language === 'ar'
                                            ? 'bg-primary text-white border-primary'
                                            : 'border-gray-300 text-gray-800 hover:bg-primary hover:text-white'
                                            }`}
                                    >
                                        🇦🇪 عربي
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </nav>
        </motion.header>
    );
}
